(() => {
    "use strict";
    const TERMS_STORAGE_KEY = "cart-terms-agreed";

    const getTermsAgreed = () => sessionStorage.getItem(TERMS_STORAGE_KEY) === "true";

    const setTermsAgreed = (agreed) => {
        sessionStorage.setItem(TERMS_STORAGE_KEY, agreed ? "true" : "false");
        document.querySelectorAll("[data-cart-checkout-agree]").forEach((checkbox) => {
            checkbox.checked = agreed;
        });
        document.querySelectorAll("dynamic-cart").forEach((cartEl) => {
            if (typeof cartEl.bindCheckoutTerms === "function") {
                cartEl.bindCheckoutTerms();
            }
        });
    };

    const updateCartCount = (cart) => {
        if (!cart || typeof cart.item_count === "undefined") return;
        document.querySelectorAll("[data-cart-item-count]").forEach((el) => {
            el.textContent = cart.item_count;
        });
        document.querySelectorAll("[data-cart-count]").forEach((el) => {
            el.textContent = cart.item_count;
            el.classList.toggle("is-zero", cart.item_count < 1);
        });
        document.querySelectorAll("dynamic-cart").forEach((cartEl) => {
            if (typeof cartEl.bindCheckoutTerms === "function") {
                cartEl.bindCheckoutTerms();
            }
        });
    };

    const handleCartResponse = async (response, suppressOpen) => {
        updateCartCount(response);
        if (response) {
            if (response.description && response.status !== 200) {
                window.toast.error({ message: response.description, icon: !1 });
            } else if (!suppressOpen) {
                window.dispatchEvent(new Event("dynamic-cart-open"));
            }
        }
        window.dispatchEvent(
            new CustomEvent("ajax-cart", {
                detail: {
                    cartSections: response ? response.sections : null,
                    item_count: response && typeof response.item_count !== "undefined" ? response.item_count : null,
                },
            })
        );
    };

    const changeCartItem = async (lineKey, quantity) => {
        window.dispatchEvent(new Event("dynamic-cart-loading"));
        const path = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        const response = await fetch(path + "cart/change.js", {
            method: "POST",
            body: JSON.stringify({
                id: lineKey,
                quantity,
                sections: ["main", "dynamic-cart"],
            }),
            headers: { "Content-Type": "application/json" },
        });
        const cart = await response.json();
        await handleCartResponse(cart, true);
    };

    class DynamicCart extends HTMLElement {
        constructor() {
            super();
            this.isDrawer = !this.classList.contains("main-cart");
            this.closeButton = this.querySelector("[data-close-dynamic-cart]");
            this.wrapper = this.querySelector("[data-dynamic-cart-wrapper]");
            this.itemsList = this.querySelector("[data-items-list]");
            this.cartFooter = this.querySelector("[data-cart-footer]");
            this.form = this.querySelector("form");

            if (this.closeButton) {
                this.closeButton.addEventListener("click", this.closeCart.bind(this));
            }
            window.addEventListener("dynamic-cart-loading", this.loading.bind(this));
            window.addEventListener("dynamic-cart-open", this.openCart.bind(this));
            window.addEventListener("ajax-cart", this.updateCart.bind(this));
            this.addEventListener("click", this.closeCart.bind(this));
            if (this.wrapper) {
                this.wrapper.addEventListener("click", (event) => {
                    event.stopPropagation();
                });
            }
            this.initItemsInteractions();
            this.bindCheckoutTerms();
        }

        bindCheckoutTerms() {
            const agreeCheckbox = this.querySelector("[data-cart-checkout-agree]");
            const checkoutButton = this.querySelector("[data-cart-checkout-button]");
            if (!agreeCheckbox || !checkoutButton) return;

            agreeCheckbox.checked = getTermsAgreed();

            const updateCheckoutState = () => {
                const countEl = this.querySelector("[data-cart-item-count]");
                let itemCount = parseInt((countEl && countEl.textContent) || "0", 10);
                if (!itemCount && !this.isDrawer) {
                    itemCount = this.querySelectorAll(".dynamic-cart__item").length;
                }
                agreeCheckbox.disabled = itemCount < 1;
                if (itemCount < 1) {
                    sessionStorage.setItem(TERMS_STORAGE_KEY, "false");
                    agreeCheckbox.checked = false;
                }
                checkoutButton.disabled = !(itemCount > 0 && agreeCheckbox.checked);
            };

            if (this._checkoutTermsHandler) {
                agreeCheckbox.removeEventListener("change", this._checkoutTermsHandler);
            }
            this._checkoutTermsHandler = (event) => {
                setTermsAgreed(event.currentTarget.checked);
            };
            agreeCheckbox.addEventListener("change", this._checkoutTermsHandler);
            updateCheckoutState();
        }

        initItemsInteractions() {
            this.quantityInputs = Array.from(this.querySelectorAll("[data-input-item-key]"));
            this.removeButtons = Array.from(this.querySelectorAll("[data-remove-item]"));
            this.quantityChangeFunction = this.quantityChange.bind(this);
            this.quantityInputs.forEach((input) => {
                input.removeEventListener("change", this.quantityChangeFunction);
                input.addEventListener("change", this.quantityChangeFunction);
            });
            this.removeButtons.forEach((button) => {
                button.removeEventListener("click", this.removeItem);
                button.addEventListener("click", this.removeItem);
            });
        }

        closeCart(event) {
            if (!this.isDrawer) return;
            event.preventDefault();
            this.removeAttribute("opened");
            this.setAttribute("closed", "");
        }

        openCart() {
            if (!this.isDrawer) return;
            this.setAttribute("opened", "");
            this.removeAttribute("closed");
        }

        loading() {
            if (!this.isDrawer || !this.form) return;
            this.form.classList.add("loading-state", "loading");
        }

        quantityChange(event) {
            const input = event.currentTarget;
            changeCartItem(input.dataset.inputItemKey, input.value);
        }

        removeItem(event) {
            event.preventDefault();
            const button = event.currentTarget;
            changeCartItem(button.dataset.removeItem, 0);
        }

        updateCart(event) {
            if (!this.form) return;
            this.form.classList.remove("loading-state", "loading");

            const sections = event.detail.cartSections;
            const sectionKey = this.dataset.cartSection || (this.isDrawer ? "dynamic-cart" : "main");
            const sectionHtml = sections ? sections[sectionKey] : null;
            if (!sectionHtml) return;

            const parsed = document.createElement("div");
            parsed.innerHTML = sectionHtml.replaceAll("dynamic-cart>", "div>");
            const itemsList = parsed.querySelector("[data-items-list]");
            const cartFooter = parsed.querySelector("[data-cart-footer]");
            if (!itemsList || !cartFooter) return;

            if (this.itemsList) this.itemsList.innerHTML = itemsList.innerHTML;
            if (this.cartFooter) this.cartFooter.innerHTML = cartFooter.innerHTML;
            this.initItemsInteractions();
            this.bindCheckoutTerms();
        }
    }

    if (!customElements.get("dynamic-cart")) {
        customElements.define("dynamic-cart", DynamicCart);
    }
})();
